import axios from 'axios'
import config from '@/config.js'

const responseCache = new Map()

function trimSlashes(value, leading = false) {
  const trimmed = String(value || '').replace(/^\/+|\/+$/g, '')
  return leading ? `/${trimmed}` : trimmed
}

function staticUrl(path) {
  return `${String(config.apiUrlClientSide || '').replace(/\/+$/g, '')}${trimSlashes(path, true)}`
}

function detailUrl(entity, id, sharded = true) {
  const stringId = String(id)
  const subfolder = sharded ? `/${encodeURIComponent(stringId.slice(0, 2))}` : ''
  return staticUrl(`${entity}${subfolder}/${encodeURIComponent(stringId)}.json`)
}

function staticKey(value) {
  const bytes = encodeURIComponent(String(value)).replace(
    /%([0-9A-F]{2})/g,
    (_, hex) => String.fromCharCode(parseInt(hex, 16))
  )
  return `hex-${[...bytes].map(
    byte => byte.charCodeAt(0).toString(16).padStart(2, '0')
  ).join('')}`
}

function get(url, options = {}) {
  const cache = options.cache !== false
  if (!cache) {
    return axios.get(url, options.axios)
  }

  const cacheKey = axios.getUri({
    ...(options.axios || {}),
    url,
  })
  if (!responseCache.has(cacheKey)) {
    const request = axios.get(url, options.axios).catch(error => {
      responseCache.delete(cacheKey)
      throw error
    })
    responseCache.set(cacheKey, request)
  }
  return responseCache.get(cacheKey)
}

function getStatic(path, options) {
  return get(staticUrl(path), options)
}

function getStaticDetail(entity, id, options = {}) {
  return get(detailUrl(entity, id, options.sharded !== false), options)
}

function isUrlWithinBase(url, baseUrl) {
  if (!url || !baseUrl) {
    return false
  }

  try {
    const pageBase = window.location.origin
    const requestUrl = new URL(url, pageBase)
    const base = new URL(baseUrl, pageBase)
    const basePath = base.pathname.replace(/\/+$/g, '')
    return requestUrl.origin === base.origin &&
      (
        requestUrl.pathname === basePath ||
        requestUrl.pathname.startsWith(`${basePath}/`)
      )
  } catch (_) {
    return false
  }
}

function isBackendUrl(url) {
  if (isUrlWithinBase(url, config.apiUrlClientSide)) {
    return false
  }
  return isUrlWithinBase(url, config.apiUrl)
}

function clearStaticCache() {
  responseCache.clear()
}

function installStaticBackendGuard(onBlocked) {
  if (!config.staticData || axios.__legamStaticBackendGuardInstalled) {
    return
  }

  axios.interceptors.request.use(request => {
    if (isBackendUrl(request.url)) {
      const error = new Error(`Backend request blocked in static mode: ${request.method || 'get'} ${request.url}`)
      error.code = 'STATIC_MODE_BACKEND_REQUEST'
      if (onBlocked) {
        onBlocked(error)
      }
      return Promise.reject(error)
    }
    return request
  })
  axios.__legamStaticBackendGuardInstalled = true
}

function readOnlyError(action) {
  const error = new Error(`${action || 'This action'} is unavailable in read-only mode`)
  error.code = 'STATIC_MODE_READ_ONLY'
  return Promise.reject(error)
}

export {
  clearStaticCache,
  detailUrl,
  get,
  getStatic,
  getStaticDetail,
  installStaticBackendGuard,
  isBackendUrl,
  readOnlyError,
  staticKey,
  staticUrl,
}
